/**
 * Design System - Color Tokens
 * 
 * Centralized color palette for the startup's internal UI (landing page, dashboard, edit panels, settings).
 * DO NOT use these colors in user-facing website components (those are in /websites/ folder).
 * 
 * Brand Identity:
 * - Primary: Blue (#60a5fa, #2563eb, #1e40af) - Trust, professionalism, technology
 * - Accent: Green (#4ade80, #22c55e) - Success, growth, positive actions
 * - Neutral: Grays for text, borders, backgrounds
 * 
 * Usage:
 *   import { colors } from '../design-system/tokens/colors';
 *   <div style={{ backgroundColor: colors.primary[600] }} />
 */

/**
 * Primary Color Scale (Blue)
 * 
 * Used for:
 * - Primary buttons and CTAs
 * - Navigation bars and headers
 * - Links and interactive elements
 * - Brand identity throughout the app
 * 
 * Existing usage: Navbar gradient, login/register pages, primary buttons
 */
export const primary = {
  50: '#eff6ff',   // Very light blue - subtle backgrounds
  100: '#dbeafe',  // Light blue - hover states, selection highlights
  200: '#bfdbfe',  // Soft blue - borders, badges
  300: '#93c5fd',  // Medium light blue - secondary elements
  400: '#60a5fa',  // Brand blue (existing) - icons, secondary buttons
  500: '#3b82f6',  // Core blue - default interactive elements
  600: '#2563eb',  // Brand primary (existing) - primary buttons, main CTAs
  700: '#1d4ed8',  // Dark blue - button hover states
  800: '#1e40af',  // Brand dark (existing) - gradient ends, dark themes
  900: '#1e3a8a',  // Very dark blue - text, strong emphasis
};

/**
 * Accent Color Scale (Green)
 * 
 * Used for:
 * - Success messages and states
 * - Positive actions (publish, save, confirm)
 * - Growth indicators and positive metrics
 * - Active/online status
 * 
 * Existing usage: Success messages, "Create" buttons, status indicators
 */
export const accent = {
  50: '#f0fdf4',   // Very light green - subtle success backgrounds
  100: '#dcfce7',  // Light green - success message backgrounds
  200: '#bbf7d0',  // Soft green - borders on success states
  300: '#86efac',  // Medium light green
  400: '#4ade80',  // Brand green (existing) - success buttons, positive CTAs
  500: '#22c55e',  // Core green - default success state
  600: '#16a34a',  // Darker green - hover states
  700: '#15803d',  // Dark green
  800: '#166534',  // Very dark green
  900: '#14532d',  // Deepest green
};

/**
 * Neutral Color Scale (Grays)
 * 
 * Used for:
 * - Text (900, 800, 700 for headings; 600, 500 for body)
 * - Borders (300, 200)
 * - Backgrounds (50, 100 for subtle areas)
 * - Disabled states (400, 300)
 * 
 * Existing usage: Extensive use throughout UI for text, borders, backgrounds
 */
export const neutral = {
  50: '#f9fafb',   // Off-white backgrounds
  100: '#f3f4f6',  // Light gray backgrounds, hover states
  200: '#e5e7eb',  // Borders, dividers
  300: '#d1d5db',  // Disabled borders, secondary borders
  400: '#9ca3af',  // Disabled text, placeholders
  500: '#6b7280',  // Secondary text
  600: '#4b5563',  // Body text
  700: '#374151',  // Dark text
  800: '#1f2937',  // Headings, emphasis
  900: '#111827',  // Strong headings, primary text
};

/**
 * Semantic Colors
 * 
 * Color assignments for specific meanings across the app.
 * These provide context and improve user understanding.
 */
export const semantic = {
  /**
   * Success - Positive outcomes, confirmations, completion
   */
  success: {
    light: accent[100],   // Background for success messages
    base: accent[400],    // Success icons, borders
    dark: accent[600],    // Success text, strong emphasis
  },
  
  /**
   * Warning - Caution, needs attention, reversible actions
   */
  warning: {
    light: '#fef3c7',     // Light amber background
    base: '#f59e0b',      // Amber 500 - warning icons, borders
    dark: '#d97706',      // Amber 600 - warning text
  },
  
  /**
   * Error - Errors, destructive actions, validation failures
   */
  error: {
    light: '#fee2e2',     // Light red background
    base: '#ef4444',      // Red 500 - error icons, borders
    dark: '#dc2626',      // Red 600 - error text
  },
  
  /**
   * Info - Information, neutral notifications, tips
   */
  info: {
    light: primary[100],  // Light blue background
    base: primary[500],   // Blue icons, borders
    dark: primary[700],   // Info text
  },
};

/**
 * Gradient Definitions
 * 
 * Pre-defined gradients used throughout the startup UI.
 * Consistent gradient usage maintains visual coherence.
 */
export const gradients = {
  /**
   * Primary Gradient - Main brand gradient
   * Used in: Navbar, auth pages, landing hero, footer
   */
  primary: {
    from: primary[600],     // #2563eb
    to: primary[800],       // #1e40af
    css: 'linear-gradient(to right, #2563eb, #1e40af)',
    tailwind: 'bg-gradient-to-r from-blue-600 to-blue-800',
  },
  
  /**
   * Success Gradient - For positive actions and success states
   */
  success: {
    from: accent[400],      // #4ade80
    to: accent[600],        // #16a34a
    css: 'linear-gradient(to right, #4ade80, #16a34a)',
    tailwind: 'bg-gradient-to-r from-green-400 to-green-600',
  },
  
  /**
   * Subtle Gradient - For cards and subtle backgrounds
   */
  subtle: {
    from: neutral[50],      // #f9fafb
    to: neutral[100],       // #f3f4f6
    css: 'linear-gradient(to bottom, #f9fafb, #f3f4f6)',
    tailwind: 'bg-gradient-to-b from-gray-50 to-gray-100',
  },
};

/**
 * Opacity/Alpha Variants
 * 
 * Common opacity values for overlays, shadows, and transparency effects.
 */
export const opacity = {
  // Overlays and backdrops
  backdrop: 0.5,        // 50% - modal backdrops
  overlayLight: 0.1,    // 10% - subtle hover overlays
  overlayMedium: 0.2,   // 20% - selected state overlays
  overlayHeavy: 0.6,    // 60% - strong overlays
  
  // Disabled states
  disabled: 0.5,        // 50% - disabled elements
  
  // Shadows and depth
  shadowLight: 0.1,     // 10% - light shadows
  shadowMedium: 0.25,   // 25% - medium shadows
  shadowHeavy: 0.4,     // 40% - strong shadows
};

/**
 * Surface Colors - Theme-aware background colors
 * 
 * Use these instead of direct bg-white, bg-gray-50, etc.
 * Automatically adapts between light and dark themes.
 */
export const surfaces = {
  /** Primary surface - Main content areas, panels, cards */
  primary: 'bg-white dark:bg-dark-950',
  
  /** Secondary surface - Sidebars, secondary panels */
  secondary: 'bg-gray-50 dark:bg-dark-900',
  
  /** Tertiary surface - Nested cards, sub-panels */
  tertiary: 'bg-gray-100 dark:bg-dark-800',
  
  /** Accent surface - Headers, navbars (currently blue) */
  accent: 'bg-blue-600 dark:bg-dark-800',
  
  /** Elevated surface - Modals, popovers, dropdowns */
  elevated: 'bg-white dark:bg-dark-900',
  
  /** Hover states */
  hover: 'hover:bg-gray-50 dark:hover:bg-dark-800',
  
  /** Active/selected states */
  active: 'bg-gray-100 dark:bg-dark-700',

  /** Glassmorphism surface - Modern navbars and sticky headers */
  glass: 'bg-white/80 dark:bg-dark-900/80 backdrop-blur-md',

  /** Glassmorphism card - Modern floating cards and form containers */
  glassCard: 'bg-white/80 dark:bg-dark-900/80 backdrop-blur-xl',

  /** Modern app background - Clean slate background for modern layouts */
  modernApp: 'bg-slate-50 dark:bg-dark-950',
};

/**
 * Border Colors - Theme-aware borders
 */
export const borders = {
  /** Default border */
  default: 'border-gray-200 dark:border-dark-700',
  
  /** Light border - subtle dividers */
  light: 'border-gray-100 dark:border-dark-800',
  
  /** Strong border - emphasis */
  strong: 'border-gray-300 dark:border-dark-600',
  
  /** Focus border */
  focus: 'border-blue-500 dark:border-blue-400',

  /** Glassmorphism border - Subtle semi-transparent borders for glass elements */
  glass: 'border-slate-200/50 dark:border-dark-700/50',
};

/**
 * Text Colors - Theme-aware text with comprehensive grayscale palette
 * 
 * Provides a full range of text colors that work in both light and dark modes.
 * In dark mode, offers 6 distinct shades from almost-white to gray for nuanced typography.
 * 
 * Light Mode: Uses standard grays (gray-900 → gray-400)
 * Dark Mode: Uses custom dark palette (dark-50 → dark-500) for better readability
 * 
 * DARK MODE SCALE (from brightest to darkest):
 * - brightest (dark-50): Almost white, maximum contrast - use sparingly for key headlines
 * - bright (dark-100): Very light gray, high emphasis - for important headings
 * - primary (dark-200): Light gray, standard emphasis - default for headings/primary text
 * - secondary (dark-300): Medium-light gray - body text, descriptions
 * - tertiary (dark-400): Medium gray - supporting text, less important content
 * - muted (dark-500): Darker gray - hints, placeholders, disabled text
 * 
 * @example
 * // Hero headline (maximum contrast)
 * <h1 className={`${typography.h1} ${textColors.brightest}`}>Welcome</h1>
 * 
 * // Section heading (high emphasis)
 * <h2 className={`${typography.h2} ${textColors.bright}`}>Features</h2>
 * 
 * // Standard heading
 * <h3 className={`${typography.h3} ${textColors.primary}`}>About Us</h3>
 * 
 * // Body text
 * <p className={`${typography.body} ${textColors.secondary}`}>Description text</p>
 * 
 * // Supporting text
 * <span className={`${typography.caption} ${textColors.tertiary}`}>Last updated</span>
 * 
 * // Muted/disabled text
 * <span className={`${typography.caption} ${textColors.muted}`}>Optional field</span>
 */
export const textColors = {
  /** Brightest text - Almost white in dark mode, maximum contrast
   *  USE FOR: Hero headlines, critical call-outs, maximum emphasis elements
   *  LIGHT: gray-900 (almost black)
   *  DARK: dark-50 (almost white) */
  brightest: 'text-gray-900 dark:text-dark-50',
  
  /** Bright text - Very light gray in dark mode, high emphasis
   *  USE FOR: Important headings (h1, h2), key section titles
   *  LIGHT: gray-800
   *  DARK: dark-100 (very light gray) */
  bright: 'text-gray-800 dark:text-dark-100',
  
  /** Primary text - Light gray in dark mode, standard emphasis
   *  USE FOR: Standard headings (h3-h6), primary content, card titles
   *  LIGHT: gray-900
   *  DARK: dark-200 (light gray) */
  primary: 'text-gray-900 dark:text-dark-200',
  
  /** Secondary text - Medium-light gray in dark mode
   *  USE FOR: Body text, descriptions, most readable content
   *  LIGHT: gray-700
   *  DARK: dark-300 (medium-light gray) */
  secondary: 'text-gray-700 dark:text-dark-300',
  
  /** Tertiary text - Medium gray in dark mode
   *  USE FOR: Supporting text, less important content, metadata
   *  LIGHT: gray-600
   *  DARK: dark-400 (medium gray) */
  tertiary: 'text-gray-600 dark:text-dark-400',
  
  /** Muted text - Darker gray in dark mode
   *  USE FOR: Hints, placeholders, disabled text, timestamps
   *  LIGHT: gray-500
   *  DARK: dark-500 (darker gray) */
  muted: 'text-gray-500 dark:text-dark-500',
  
  /** Inverse text - For colored backgrounds
   *  USE FOR: Text on dark backgrounds (light mode), text on light backgrounds (dark mode)
   *  LIGHT: white
   *  DARK: gray-900 */
  inverse: 'text-white dark:text-gray-900',
  
  /** On-light text - For text that sits on light surfaces in BOTH themes
   *  USE FOR: Light pills/badges/buttons that remain light even in dark mode (e.g., light-yellow status chips)
   *  LIGHT: gray-900
   *  DARK: gray-900 */
  onLight: 'text-gray-900 dark:text-gray-900',

  /** Hover state for text links and interactive text */
  hover: 'hover:text-gray-700 dark:hover:text-dark-200',
};

/**
 * Interactive Colors - Buttons, links, hover states
 */
export const interactive = {
  /** Primary button background */
  primaryBg: 'bg-gradient-to-r from-blue-600 to-sky-500',
  primaryHover: 'hover:from-blue-700 hover:to-sky-600',
  primaryText: 'text-white dark:text-white',
  
  /** Secondary button */
  secondaryBg: 'bg-gray-200 dark:bg-dark-700',
  secondaryHover: 'hover:bg-gray-300 dark:hover:bg-dark-600',
  secondaryText: 'text-gray-900 dark:text-dark-100',
  
  /** Danger/Destructive button (delete, remove, etc.) */
  dangerBg: 'bg-red-600 dark:bg-red-500',
  dangerHover: 'hover:bg-red-700 dark:hover:bg-red-600',
  dangerText: 'text-white dark:text-white',
  
  /** Success button (confirm, save, etc.) */
  successBg: 'bg-green-600 dark:bg-green-500',
  successHover: 'hover:bg-green-700 dark:hover:bg-green-600',
  successText: 'text-white dark:text-white',
  
  /** Link colors */
  link: 'text-blue-600 dark:text-blue-400',
  linkHover: 'hover:text-blue-800 dark:hover:text-blue-300',
  
  /** Modern gradient button - Blue to Sky */
  primaryGradientBtn: 'bg-gradient-to-r from-blue-600 to-sky-500 hover:from-blue-700 hover:to-sky-600 text-white shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all',
  
  /** Selected option background – vibrant gradient in light, neutral surface in dark */
  selectedBg: 'bg-gradient-to-r from-blue-600 to-sky-500 dark:bg-none dark:bg-dark-600',
  /** Selected option border – invisible in light, blue accent in dark */
  selectedBorder: 'border-transparent dark:border-blue-400',

  /** Modern glass button */
  glassBtn: 'bg-white/50 dark:bg-dark-800/50 hover:bg-white/80 dark:hover:bg-dark-700/80 backdrop-blur-sm transition-all',
  
  /** Modern icon button (glass style) */
  glassIconBtn: 'p-2 text-gray-500 hover:bg-white/50 dark:hover:bg-dark-800/50 hover:text-gray-900 dark:hover:text-white rounded-xl transition-all',
};

/**
 * Visual Effects - Glows, orbs, and decorative elements
 * 
 * Used to add depth and modern aesthetics to backgrounds.
 */
export const effects = {
  /** Subtle background glow effect (blue orb) */
  backgroundGlowBlue: 'absolute top-[-10%] left-[-10%] w-[40vw] h-[40vw] rounded-full bg-blue-400/10 dark:bg-blue-600/10 blur-[80px] mix-blend-multiply dark:mix-blend-screen',
  
  /** Subtle background glow effect (sky orb) */
  backgroundGlowSky: 'absolute bottom-[-10%] right-[-10%] w-[50vw] h-[50vw] rounded-full bg-sky-300/10 dark:bg-sky-500/10 blur-[100px] mix-blend-multiply dark:mix-blend-screen',
  
  /** Animated floating orb for hero sections (blue) */
  heroGlowBlue: 'absolute top-1/2 left-1/4 -translate-y-1/2 w-64 h-64 bg-blue-400/20 dark:bg-blue-500/20 rounded-full blur-[80px] mix-blend-multiply dark:mix-blend-screen animate-[float_8s_ease-in-out_infinite]',
  
  /** Animated floating orb for hero sections (sky) */
  heroGlowSky: 'absolute top-1/2 right-1/4 -translate-y-1/2 w-72 h-72 bg-sky-300/20 dark:bg-sky-400/20 rounded-full blur-[80px] mix-blend-multiply dark:mix-blend-screen animate-[float_10s_ease-in-out_infinite_reverse]',
};

/**
 * Brand Colors - Theme-aware brand colors for Landing Pages
 * 
 * These colors automatically switch from blue (light mode) to sky (dark mode)
 * for landing pages, hero sections, and marketing content.
 * 
 * Light Mode: Blue (trust, professionalism, technology)
 * Dark Mode: Sky (cool, modern, high-contrast highlight)
 * 
 * @example
 * // Landing page hero
 * <div className={`${brandColors.primaryBg} text-white p-8`}>
 *   <h1>Welcome</h1>
 * </div>
 * 
 * // Gradient hero
 * <div className={`bg-gradient-to-r ${brandColors.gradientFrom} ${brandColors.gradientTo}`}>
 *   <h1>Create Your Portfolio</h1>
 * </div>
 */
export const brandColors = {
  /** Primary brand text color - blue in light, blue in dark */
  primary: 'text-blue-600 dark:text-blue-600',
  
  /** Primary brand text hover */
  primaryHover: 'hover:text-blue-700 dark:hover:text-blue-700',
  
  /** Light variant for backgrounds */
  primaryLight: 'bg-blue-50 dark:bg-blue-50',
  
  /** Light variant for backgrounds (with transparency in dark mode) */
  primaryBgLight: 'bg-blue-50 dark:bg-blue-50/20',
  
  /** Dark variant for text */
  primaryDark: 'text-blue-800 dark:text-blue-800',
  
  /** Primary background color */
  primaryBg: 'bg-gradient-to-r from-blue-600 to-sky-500',
  
  /** Primary background hover */
  primaryBgHover: 'hover:from-blue-700 hover:to-sky-600',
  
  /** Secondary background (darker) */
  primaryBgDark: 'bg-blue-800 dark:bg-blue-800',
  
  /** Gradient start point */
  gradientFrom: 'from-blue-600 dark:from-blue-600',
  
  /** Gradient end point */
  gradientTo: 'to-blue-800 dark:to-blue-800',
  
  /** Modern brand gradient text - Vibrant blue to sky blue */
  gradientText: 'bg-gradient-to-r from-blue-600 via-blue-500 to-sky-400 text-transparent bg-clip-text',
  
  /** Border color */
  borderColor: 'border-blue-300 dark:border-blue-300',
  
  /** Primary border color (for active/selected states) */
  primaryBorder: 'border-blue-500 dark:border-blue-500',
  
  /** Border hover color */
  borderHover: 'hover:border-blue-400 dark:hover:border-blue-400',
  
  /** Focus ring color */
  focus: 'focus:ring-blue-500 dark:focus:ring-blue-500',
  
  /** Light hover background (for buttons on colored backgrounds) */
  hoverLight: 'hover:bg-blue-700 dark:hover:bg-blue-700',
  
  /** Dark hover background (for gradient backgrounds) */
  hoverDark: 'hover:bg-blue-900 dark:hover:bg-blue-900',

  /**
   * Marketing wave background helpers (Landing/Auth/Dashboard hero sections)
   * Keep these centralized so theme highlight changes are one edit.
   */
  waveGradientBg: 'bg-gradient-to-r from-blue-600 to-blue-800 dark:from-blue-600 dark:to-blue-800',
  waveGradientAnimated: 'bg-gradient-to-r from-blue-600 to-blue-800 dark:from-blue-600 dark:to-blue-800 animate-gradient',
  waveSurfaceInverseBg: 'bg-white dark:bg-dark-950',
  // SVG fill colors (use arbitrary rgba for exact alpha control)
  waveFill1: 'fill-[rgba(255,255,255,0.12)]',
  waveFill2: 'fill-[rgba(255,255,255,0.20)]',
  waveInverseFill1: 'fill-[rgba(37,99,235,0.12)] dark:fill-[rgba(37,99,235,0.12)]',
  waveInverseFill2: 'fill-[rgba(37,99,235,0.20)] dark:fill-[rgba(37,99,235,0.20)]',
};

/**
 * Consolidated colors export
 * 
 * Main export containing all color scales and utilities.
 */
export const colors = {
  primary,
  accent,
  neutral,
  semantic,
  gradients,
  opacity,
  
  // Theme-aware tokens
  surfaces,
  borders,
  textColors,
  interactive,
  brandColors,
  effects,
  
  // Aliases for common use cases
  brand: primary,       // Brand colors are primary blues
  gray: neutral,        // Grays are neutrals
  success: accent,      // Success colors are accent greens
};

/**
 * Default export for convenience
 */
export default colors;
